import { useEffect, useMemo, useState } from "react";
import type React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import {
  ArrowLeft,
  BarChart3,
  CheckCircle2,
  Copy,
  CreditCard,
  ExternalLink,
  Loader2,
  MessageCircle,
  PackagePlus,
  Plus,
  QrCode,
  ReceiptText,
  Send,
  Settings,
  ShoppingBag,
  Sparkles,
  Store,
  Users,
} from "lucide-react";
import { Link } from "@/lib/router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  addLoyaltyStamp,
  createTienditaEntity,
  createTienditaWorkspace,
  getAccount,
  getTienditaBundle,
  importTienditaCatalogText,
  listMyTienditaWorkspaces,
  patchTienditaProfile,
  queueTienditaAbandonmentReminders,
  requestMagicLink,
  runTienditaCommerceTool,
  startSubscriptionCheckout,
  type AccountContext,
  type TienditaBundle,
  upsertIntegration,
  verifyMagicToken,
} from "./tienditaApi";

const TOKEN_KEY = "ai-suite-account-token";

type VerticalKey = "comida" | "ropa" | "belleza" | "repuestos" | "servicios";

type VerticalTemplate = {
  id: VerticalKey;
  label: string;
  tagline: string;
  hero: string;
  firstReply: string;
  categories: string[];
  products: Array<{ name: string; price: string; stock: string; description: string }>;
  offer: string;
};

const VERTICAL_TEMPLATES: Record<VerticalKey, VerticalTemplate> = {
  comida: {
    id: "comida",
    label: "Comida",
    tagline: "Menú, domicilios y combos listos para WhatsApp.",
    hero: "Menú del día",
    firstReply: "Hola, te paso el menú de hoy. ¿Quieres domicilio o recoges en tienda?",
    categories: ["Combos", "Bebidas", "Postres"],
    offer: "Combo recomendado + bebida por tiempo limitado.",
    products: [
      { name: "Almuerzo ejecutivo", price: "18000", stock: "25", description: "Proteína, principio, ensalada y bebida." },
      { name: "Combo hamburguesa", price: "26000", stock: "18", description: "Hamburguesa artesanal con papas." },
      { name: "Jugo natural", price: "7000", stock: "40", description: "Mora, mango o lulo." },
    ],
  },
  ropa: {
    id: "ropa",
    label: "Ropa",
    tagline: "Tallas, colores, drops y catálogo por link.",
    hero: "Nueva colección",
    firstReply: "Hola, te muestro tallas disponibles. ¿Buscas algo casual, oficina o salida?",
    categories: ["Camisetas", "Jeans", "Accesorios"],
    offer: "Envío gratis desde 2 prendas.",
    products: [
      { name: "Camiseta negra oversize", price: "45000", stock: "12", description: "Algodón, tallas S a XL." },
      { name: "Jean recto azul", price: "98000", stock: "8", description: "Denim rígido, tiro alto." },
      { name: "Gorra beige", price: "38000", stock: "15", description: "Ajustable, bordado frontal." },
    ],
  },
  belleza: {
    id: "belleza",
    label: "Belleza",
    tagline: "Agenda, productos, kits y recompra automática.",
    hero: "Kit de cuidado",
    firstReply: "Hola, puedo ayudarte con productos o reservarte una cita. ¿Qué necesitas hoy?",
    categories: ["Skincare", "Maquillaje", "Citas"],
    offer: "Diagnóstico rápido y rutina sugerida por WhatsApp.",
    products: [
      { name: "Serum vitamina C", price: "68000", stock: "10", description: "30 ml, uso diario." },
      { name: "Kit cejas", price: "52000", stock: "14", description: "Gel, cepillo y perfilador." },
      { name: "Limpieza facial", price: "90000", stock: "6", description: "Servicio con reserva previa." },
    ],
  },
  repuestos: {
    id: "repuestos",
    label: "Repuestos",
    tagline: "Cotización rápida por referencia, marca o foto.",
    hero: "Repuesto confirmado",
    firstReply: "Hola, envíame referencia, modelo o foto del repuesto y reviso disponibilidad.",
    categories: ["Filtros", "Eléctricos", "Lubricantes"],
    offer: "Validación de compatibilidad antes de pagar.",
    products: [
      { name: "Filtro de aceite universal", price: "32000", stock: "20", description: "Compatible con líneas populares." },
      { name: "Bombillo H4 LED", price: "58000", stock: "16", description: "Par de luces blancas." },
      { name: "Aceite 10W40", price: "42000", stock: "24", description: "Cuarto sintético." },
    ],
  },
  servicios: {
    id: "servicios",
    label: "Servicios",
    tagline: "Reservas, anticipos, paquetes y seguimiento.",
    hero: "Agenda abierta",
    firstReply: "Hola, cuéntame qué servicio necesitas y te paso horarios disponibles.",
    categories: ["Diagnóstico", "Paquetes", "Soporte"],
    offer: "Reserva con anticipo y recordatorio automático.",
    products: [
      { name: "Diagnóstico inicial", price: "50000", stock: "10", description: "Sesión de 30 minutos." },
      { name: "Paquete mensual", price: "280000", stock: "5", description: "4 sesiones con seguimiento." },
      { name: "Soporte express", price: "85000", stock: "8", description: "Atención prioritaria el mismo día." },
    ],
  },
};

const WIZARD_STEPS = ["Vertical", "Negocio", "Catálogo", "WhatsApp", "Publicar"] as const;

export function TienditaBotApp() {
  const queryClient = useQueryClient();
  const [token, setToken] = useState(() => (typeof window === "undefined" ? "" : window.localStorage.getItem(TOKEN_KEY) || ""));
  const [email, setEmail] = useState("");
  const [notice, setNotice] = useState("");
  const [workspaceId, setWorkspaceId] = useState("");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const accountToken = hash.get("account_token");
    if (!accountToken) return;
    window.localStorage.setItem(TOKEN_KEY, accountToken);
    setToken(accountToken);
    hash.delete("account_token");
    window.history.replaceState({}, "", `${window.location.pathname}${window.location.search}${hash.toString() ? `#${hash.toString()}` : ""}`);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  }, [token]);

  const accountQuery = useQuery<AccountContext>({
    queryKey: ["tiendita-account", token],
    enabled: Boolean(token),
    queryFn: () => getAccount(token),
  });

  useEffect(() => {
    if (!accountQuery.isError) return;
    setNotice("Tu sesión expiró. Vuelve a entrar con tu email.");
    setToken("");
  }, [accountQuery.isError]);

  const workspacesQuery = useQuery({
    queryKey: ["tiendita-workspaces", token],
    enabled: Boolean(token && accountQuery.data),
    queryFn: () => listMyTienditaWorkspaces(token),
  });

  const inferredWorkspaceId = useMemo(() => {
    const subscription = accountQuery.data?.subscriptions.find((item) => item.productKind === "suite" && item.productId === "tiendita-bot-os");
    return workspaceId || String(subscription?.workspaceId || workspacesQuery.data?.workspaces[0]?.id || "");
  }, [accountQuery.data?.subscriptions, workspaceId, workspacesQuery.data?.workspaces]);

  const bundleQuery = useQuery<TienditaBundle>({
    queryKey: ["tiendita-bundle", token, inferredWorkspaceId],
    enabled: Boolean(token && inferredWorkspaceId),
    queryFn: () => getTienditaBundle(token, inferredWorkspaceId),
  });

  const loginMutation = useMutation({
    mutationFn: async () => {
      const result = await requestMagicLink(email);
      if (!result.token) return result;
      const verified = await verifyMagicToken(result.token);
      setToken(verified.sessionToken);
      return { ...result, ...verified };
    },
    onSuccess: (result) => {
      if ("sessionToken" in result) {
        setNotice("Sesión iniciada. Crea o abre tu tienda.");
        return;
      }
      setNotice("Te enviamos un enlace de acceso. Revisa tu correo.");
    },
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["tiendita-bundle"] });

  if (!token) {
    return (
      <TienditaShell>
        <Card className="mx-auto max-w-xl rounded-[2rem] border-white/10 bg-white/[0.06] shadow-2xl shadow-emerald-950/20">
          <CardHeader>
            <Pill>Acceso cliente-final</Pill>
            <CardTitle className="text-3xl">Entra y deja tu tienda lista en minutos</CardTitle>
            <CardDescription>TienditaBot arma catálogo, link, QR, WhatsApp y tablero operativo sin montar un ecommerce complejo.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input value={email} onChange={(event) => setEmail(event.target.value)} placeholder="tu@email.com" className="h-12 rounded-2xl border-white/10 bg-black/20" />
            <Button className="h-12 w-full rounded-2xl bg-emerald-300 font-black text-slate-950 hover:bg-white" disabled={loginMutation.isPending || !email.trim()} onClick={() => loginMutation.mutate()}>
              {loginMutation.isPending ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <MessageCircle className="mr-2 h-5 w-5" />}
              Enviar enlace y continuar
            </Button>
            {notice || loginMutation.error ? <StatusMessage tone={loginMutation.error ? "error" : "info"}>{loginMutation.error?.message || notice}</StatusMessage> : null}
          </CardContent>
        </Card>
      </TienditaShell>
    );
  }

  if (accountQuery.isLoading) {
    return <TienditaShell><LoadingState label="Cargando cuenta..." /></TienditaShell>;
  }

  if (!inferredWorkspaceId || bundleQuery.error?.message.includes("Tiendita workspace not found")) {
    return (
      <TienditaShell>
        <Onboarding email={accountQuery.data?.user?.email || email} token={token} onCreated={(id) => {
          setWorkspaceId(id);
          void accountQuery.refetch();
          void workspacesQuery.refetch();
        }} />
      </TienditaShell>
    );
  }

  if (bundleQuery.isLoading) {
    return <TienditaShell><LoadingState label="Cargando tienda..." /></TienditaShell>;
  }

  if (bundleQuery.isError || !bundleQuery.data) {
    return (
      <TienditaShell>
        <StatusMessage tone="error">{bundleQuery.error?.message || "No se pudo cargar TienditaBot."}</StatusMessage>
      </TienditaShell>
    );
  }

  return (
    <TienditaShell bundle={bundleQuery.data}>
      <Dashboard bundle={bundleQuery.data} token={token} onRefresh={refresh} />
    </TienditaShell>
  );
}

function TienditaShell({ children, bundle }: { children: React.ReactNode; bundle?: TienditaBundle }) {
  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-16 text-white">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button asChild variant="ghost" className="rounded-full text-slate-300 hover:bg-white/10 hover:text-white">
          <Link to="/ai-suite/landings/messaging/tiendita-bot-os">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver a la landing
          </Link>
        </Button>
        {bundle ? (
          <div className="rounded-full border border-white/10 bg-white/10 px-4 py-2 text-sm text-slate-200">
            {bundle.workspace.name} · Plan {bundle.workspace.plan}
          </div>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function Onboarding({ email, token, onCreated }: { email: string; token: string; onCreated: (workspaceId: string) => void }) {
  const [step, setStep] = useState(0);
  const [catalogText, setCatalogText] = useState("");
  const [form, setForm] = useState({
    businessName: "",
    businessType: "ropa" as VerticalKey,
    country: "CO",
    currency: "COP",
    whatsappPhone: "",
    address: "",
    deliveryPromise: "Entrega hoy o mañana según disponibilidad.",
  });

  const template = VERTICAL_TEMPLATES[form.businessType];
  const previewProducts = parseCatalogText(catalogText).length ? parseCatalogText(catalogText) : template.products;

  const mutation = useMutation({
    mutationFn: async () => {
      const result = await createTienditaWorkspace(token, {
        ...form,
        ownerEmail: email,
        templateId: template.id,
        categories: template.categories,
        welcomeMessage: template.firstReply,
        storeDescription: template.tagline,
        offer: template.offer,
        publicSlug: slugify(form.businessName),
        publicStoreEnabled: true,
        manualModeAcceptedAt: new Date().toISOString(),
        paymentMethods: ["WhatsApp", "Mercado Pago/transferencia por confirmar"],
        importedCatalogText: catalogText,
        seedProducts: previewProducts.slice(0, 12).map((product) => ({
          ...product,
          category: template.categories[0],
          metadata: { source: catalogText.trim() ? "text_import_preview" : "vertical_template", templateId: template.id },
        })),
      });
      return result;
    },
    onSuccess: (result) => {
      if (result.sessionToken) window.localStorage.setItem(TOKEN_KEY, result.sessionToken);
      onCreated(result.workspace.id);
    },
  });

  const canContinue = step === 0 || (step === 1 ? form.businessName.trim() : step === 3 ? form.whatsappPhone.trim() : true);

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
      <Card className="rounded-[2rem] border-white/10 bg-white/[0.06]">
        <CardHeader>
          <Pill>Wizard plug-and-play</Pill>
          <CardTitle className="text-3xl">Configura tu TienditaBot</CardTitle>
          <CardDescription>5 pasos: vertical, datos, catálogo, WhatsApp y publicación. Puedes cambiar todo después.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <WizardProgress step={step} />
          {step === 0 ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {Object.values(VERTICAL_TEMPLATES).map((item) => (
                <button
                  key={item.id}
                  className={`rounded-3xl border p-4 text-left transition ${form.businessType === item.id ? "border-emerald-200 bg-emerald-300/15" : "border-white/10 bg-black/20 hover:border-white/30"}`}
                  onClick={() => setForm((current) => ({ ...current, businessType: item.id }))}
                  type="button"
                >
                  <p className="font-black text-white">{item.label}</p>
                  <p className="mt-2 text-sm leading-6 text-slate-300">{item.tagline}</p>
                  <p className="mt-3 text-xs uppercase tracking-[0.16em] text-emerald-200">{item.categories.join(" / ")}</p>
                </button>
              ))}
            </div>
          ) : null}
          {step === 1 ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Nombre del negocio" value={form.businessName} onChange={(businessName) => setForm((current) => ({ ...current, businessName }))} placeholder="Tienda de Ana" />
              <Field label="País" value={form.country} onChange={(country) => setForm((current) => ({ ...current, country }))} placeholder="CO" />
              <Field label="Moneda" value={form.currency} onChange={(currency) => setForm((current) => ({ ...current, currency }))} placeholder="COP" />
              <Field label="Dirección o zona" value={form.address} onChange={(address) => setForm((current) => ({ ...current, address }))} placeholder="Chapinero, Bogotá" />
            </div>
          ) : null}
          {step === 2 ? (
            <div className="space-y-4">
              <label className="space-y-1.5">
                <span className="text-xs font-bold text-slate-300">Pega tu catálogo, menú o lista de servicios</span>
                <Textarea
                  value={catalogText}
                  onChange={(event) => setCatalogText(event.target.value)}
                  placeholder={"Camiseta negra - 45000 - 10 - Algodón oversize\nJean recto - 98000 - 8 - Tiro alto"}
                  className="min-h-40 rounded-2xl border-white/10 bg-black/20"
                />
              </label>
              <StatusMessage>Si lo dejas vacío, cargamos productos base de {template.label.toLowerCase()} para que puedas publicar rápido.</StatusMessage>
              <ProductPreview products={previewProducts} />
            </div>
          ) : null}
          {step === 3 ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="WhatsApp del negocio" value={form.whatsappPhone} onChange={(whatsappPhone) => setForm((current) => ({ ...current, whatsappPhone }))} placeholder="+573001112233" />
              <Field label="Promesa de entrega/agenda" value={form.deliveryPromise} onChange={(deliveryPromise) => setForm((current) => ({ ...current, deliveryPromise }))} placeholder="Entregas el mismo día" />
              <div className="sm:col-span-2">
                <WhatsAppPreview businessName={form.businessName || "Tu tienda"} template={template} products={previewProducts} />
              </div>
            </div>
          ) : null}
          {step === 4 ? (
            <ReadyPreview businessName={form.businessName || "Tu tienda"} template={template} products={previewProducts} />
          ) : null}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button variant="outline" className="rounded-2xl border-white/10 bg-transparent" disabled={step === 0 || mutation.isPending} onClick={() => setStep((current) => Math.max(0, current - 1))}>
              Atrás
            </Button>
            {step < WIZARD_STEPS.length - 1 ? (
              <Button className="rounded-2xl bg-emerald-300 font-black text-slate-950 hover:bg-white" disabled={!canContinue} onClick={() => setStep((current) => Math.min(WIZARD_STEPS.length - 1, current + 1))}>
                Continuar
              </Button>
            ) : (
              <Button className="rounded-2xl bg-emerald-300 font-black text-slate-950 hover:bg-white" disabled={mutation.isPending || !form.businessName.trim()} onClick={() => mutation.mutate()}>
                {mutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Store className="mr-2 h-4 w-4" />}
                Crear tienda lista
              </Button>
            )}
          </div>
          {mutation.error ? <StatusMessage tone="error">{mutation.error.message}</StatusMessage> : null}
        </CardContent>
      </Card>
      <LaunchSidecar businessName={form.businessName || "Tu tienda"} template={template} products={previewProducts} />
    </div>
  );
}

function Dashboard({ bundle, token, onRefresh }: { bundle: TienditaBundle; token: string; onRefresh: () => void }) {
  const template = getTemplateFromBundle(bundle);
  const storeLink = buildStoreLink(bundle);

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-[2rem] border border-white/10 bg-[radial-gradient(circle_at_top_left,rgba(110,231,183,0.22),transparent_35%),linear-gradient(135deg,rgba(255,255,255,0.08),rgba(8,47,73,0.18))] p-6">
        <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
          <div>
            <Pill>Producto operativo</Pill>
            <h1 className="mt-3 text-4xl font-black tracking-tight sm:text-5xl">{bundle.workspace.name}</h1>
            <p className="mt-4 max-w-3xl text-lg leading-8 text-slate-300">
              Tu tienda ya puede vender por WhatsApp: catálogo, link, QR, pedidos, clientes, recibos y tablero en una sola vista.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {template.categories.map((category) => <Pill key={category}>{category}</Pill>)}
            </div>
          </div>
          <StoreReadyCard bundle={bundle} storeLink={storeLink} />
        </div>
      </section>

      <DashboardMetrics bundle={bundle} />
      <DailySummaryCard bundle={bundle} />

      <div className="grid gap-6 xl:grid-cols-[420px_1fr]">
        <div className="space-y-6">
          <WhatsAppPreview businessName={bundle.workspace.name} template={template} products={productsForPreview(bundle, template)} />
          <BillingCard bundle={bundle} token={token} />
          <ActivationChecklist bundle={bundle} />
        </div>
        <div className="space-y-6">
          <PlugAndPlayConsole bundle={bundle} token={token} template={template} onRefresh={onRefresh} />
          <DataSections bundle={bundle} token={token} onRefresh={onRefresh} />
        </div>
      </div>
    </div>
  );
}

function BillingCard({ bundle, token }: { bundle: TienditaBundle; token: string }) {
  const mutation = useMutation({
    mutationFn: () => startSubscriptionCheckout(token, {
      plan: bundle.workspace.plan === "pro" ? "Pro" : "Starter",
      workspaceId: bundle.workspace.id,
      ownerEmail: bundle.workspace.ownerEmail,
      workspaceName: bundle.workspace.name,
    }),
    onSuccess: (result) => {
      if (result.sessionToken) window.localStorage.setItem(TOKEN_KEY, result.sessionToken);
      if (result.checkoutUrl) window.location.href = result.checkoutUrl;
    },
  });
  return (
    <Card className="rounded-[1.5rem] border-white/10 bg-black/25">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <CreditCard className="h-5 w-5 text-emerald-200" />
          Cobros y plan
        </CardTitle>
        <CardDescription>{bundle.billing?.active ? "Suscripción activa" : "Plan gratis o pago pendiente"}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm">
          Plan actual: <span className="font-black text-white">{bundle.workspace.plan}</span>
        </div>
        <Button className="w-full rounded-2xl bg-white text-slate-950 hover:bg-emerald-100" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
          {mutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Activar o mejorar plan
        </Button>
        {mutation.error ? <StatusMessage tone="error">{mutation.error.message}</StatusMessage> : null}
      </CardContent>
    </Card>
  );
}

function ActivationChecklist({ bundle }: { bundle: TienditaBundle }) {
  const done = bundle.setupChecklist.filter((item) => item.done).length;
  return (
    <Card className="h-fit rounded-[1.5rem] border-white/10 bg-white/[0.055]">
      <CardHeader>
        <CardTitle>Activación</CardTitle>
        <CardDescription>{done}/{bundle.setupChecklist.length} pasos completados</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {bundle.setupChecklist.map((item) => (
          <div key={item.id} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/20 p-3 text-sm">
            <CheckCircle2 className={`h-5 w-5 ${item.done ? "text-emerald-300" : "text-slate-600"}`} />
            <span className={item.done ? "text-white" : "text-slate-400"}>{item.label}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function DashboardMetrics({ bundle }: { bundle: TienditaBundle }) {
  const salesValue = moneyFromCents(sumRecords(bundle.orders, ["totalCents", "amountCents", "total"]), bundle);
  return (
    <div className="grid gap-4 md:grid-cols-4">
      <Metric label="Ventas" value={salesValue} icon={<BarChart3 className="h-5 w-5" />} />
      <Metric label="Pedidos" value={`${bundle.orders.length || bundle.metrics.pendingOrders || 0}`} icon={<ReceiptText className="h-5 w-5" />} />
      <Metric label="Productos" value={`${bundle.products.length || bundle.metrics.products || 0}/${bundle.metrics.productLimit || "∞"}`} icon={<PackagePlus className="h-5 w-5" />} />
      <Metric label="Clientes" value={`${bundle.customers.length || bundle.metrics.optInCustomers || 0}`} icon={<Users className="h-5 w-5" />} />
    </div>
  );
}

function DailySummaryCard({ bundle }: { bundle: TienditaBundle }) {
  const summary = bundle.dailySummary || {};
  const topProducts = Array.isArray(summary.topProducts) ? summary.topProducts as Array<Record<string, unknown>> : [];
  const lowStock = Array.isArray(summary.lowStockProducts) ? summary.lowStockProducts as Array<Record<string, unknown>> : [];
  return (
    <Card className="rounded-[1.5rem] border-white/10 bg-white/[0.055]">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-emerald-200" />
          Resumen diario
        </CardTitle>
        <CardDescription>{String(summary.date || new Date().toISOString().slice(0, 10))}</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-[1fr_1fr_1fr]">
        <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Ventas</p>
          <p className="mt-2 text-2xl font-black text-white">{moneyFromCents(Number(summary.salesCents || 0), bundle)}</p>
          <p className="mt-1 text-sm text-slate-300">{String(summary.paidOrders || 0)} pagados · {String(summary.pendingOrders || 0)} pendientes</p>
        </div>
        <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Más vendidos</p>
          <div className="mt-2 space-y-1 text-sm text-slate-200">
            {topProducts.length ? topProducts.slice(0, 3).map((item) => (
              <p key={String(item.productId || item.name)}>{String(item.name)} x{String(item.quantity || 0)}</p>
            )) : <p>Sin ventas hoy</p>}
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Inventario bajo</p>
          <div className="mt-2 space-y-1 text-sm text-slate-200">
            {lowStock.length ? lowStock.slice(0, 3).map((item) => (
              <p key={String(item.id)}>{String(item.name)} · {String(item.stock || 0)} disp.</p>
            )) : <p>Sin alertas</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StoreReadyCard({ bundle, storeLink }: { bundle: TienditaBundle; storeLink: string }) {
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");
  const enabled = bundle.publicStore?.enabled !== false;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(storeLink);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
    window.setTimeout(() => setCopyState("idle"), 1600);
  };
  return (
    <Card className="rounded-[1.5rem] border-emerald-200/20 bg-emerald-300/10">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <QrCode className="h-5 w-5 text-emerald-200" />
          {enabled ? "Tienda lista para vender" : "Tienda pausada"}
        </CardTitle>
        <CardDescription>{enabled ? "Comparte este link o QR para recibir pedidos." : "Activa la tienda pública desde Perfil antes de compartir."}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="mx-auto flex h-40 w-40 items-center justify-center rounded-3xl border border-white/15 bg-white p-3">
          <QRCodeSVG value={bundle.publicStore?.qrPayload || storeLink} size={128} bgColor="#ffffff" fgColor="#0f172a" level="M" includeMargin />
        </div>
        <div className="rounded-2xl border border-white/10 bg-black/25 p-3 text-sm text-slate-200">{storeLink}</div>
        <div className="grid gap-2 sm:grid-cols-2">
          <Button className="rounded-2xl bg-white text-slate-950 hover:bg-emerald-100" onClick={copyLink} type="button">
            <Copy className="mr-2 h-4 w-4" />
            {copyState === "copied" ? "Copiado" : copyState === "failed" ? "Copia manualmente el link" : "Copiar link"}
          </Button>
          <Button asChild variant="outline" className="rounded-2xl border-white/10 bg-transparent">
            <a href={storeLink} target="_blank" rel="noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" />
              Abrir
            </a>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PlugAndPlayConsole({ bundle, token, template, onRefresh }: { bundle: TienditaBundle; token: string; template: VerticalTemplate; onRefresh: () => void }) {
  const [tab, setTab] = useState<"import" | "product" | "order" | "customer" | "broadcast" | "profile" | "ops">("import");
  return (
    <Card className="rounded-[1.5rem] border-white/10 bg-white/[0.055]">
      <CardHeader>
        <CardTitle>Centro plug-and-play</CardTitle>
        <CardDescription>Importa catálogo, crea pedidos y ajusta la tienda sin salir del panel.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-wrap gap-2">
          {[
            ["import", "Importador"],
            ["product", "Producto"],
            ["order", "Pedido"],
            ["customer", "Cliente"],
            ["broadcast", "Promo"],
            ["profile", "Perfil"],
            ["ops", "Operación"],
          ].map(([id, label]) => (
            <Button key={id} size="sm" variant={tab === id ? "default" : "outline"} className="rounded-full" onClick={() => setTab(id as typeof tab)}>
              {label}
            </Button>
          ))}
        </div>
        {tab === "import" ? <CatalogImporter bundle={bundle} token={token} template={template} onRefresh={onRefresh} /> : null}
        {tab === "product" ? <ProductForm bundle={bundle} token={token} onRefresh={onRefresh} /> : null}
        {tab === "order" ? <OrderForm bundle={bundle} token={token} onRefresh={onRefresh} /> : null}
        {tab === "customer" ? <CustomerForm bundle={bundle} token={token} onRefresh={onRefresh} /> : null}
        {tab === "broadcast" ? <BroadcastForm bundle={bundle} token={token} onRefresh={onRefresh} /> : null}
        {tab === "profile" ? <ProfileForm bundle={bundle} token={token} onRefresh={onRefresh} /> : null}
        {tab === "ops" ? <OpsForm bundle={bundle} token={token} onRefresh={onRefresh} /> : null}
      </CardContent>
    </Card>
  );
}

function CatalogImporter({ bundle, token, template, onRefresh }: { bundle: TienditaBundle; token: string; template: VerticalTemplate; onRefresh: () => void }) {
  const [text, setText] = useState(template.products.map((product) => `${product.name} - ${product.price} - ${product.stock} - ${product.description}`).join("\n"));
  const parsed = parseCatalogText(text);
  const mutation = useMutation({
    mutationFn: () => importTienditaCatalogText(token, bundle.workspace.id, {
      text,
      vertical: template.id === "ropa" ? "moda" : template.id,
      currency: bundle.profile.currency || "COP",
    }),
    onSuccess: onRefresh,
  });
  return (
    <div className="space-y-4">
      <label className="space-y-1.5">
        <span className="text-xs font-bold text-slate-300">Importador desde texto</span>
        <Textarea value={text} onChange={(event) => setText(event.target.value)} className="min-h-40 rounded-2xl border-white/10 bg-black/20" />
      </label>
      <ProductPreview products={parsed} />
      <Button className="h-11 rounded-2xl bg-emerald-300 font-black text-slate-950 hover:bg-white" disabled={!parsed.length || mutation.isPending} onClick={() => mutation.mutate()}>
        {mutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
        Importar {parsed.length} producto(s)
      </Button>
      {mutation.error ? <StatusMessage tone="error">{mutation.error.message}</StatusMessage> : null}
    </div>
  );
}

function ProductForm({ bundle, token, onRefresh }: FormProps) {
  const [payload, setPayload] = useState({ name: "", price: "", stock: "0", description: "", category: "", variantsText: "", modifiersText: "", imageUrl: "" });
  const mutation = useCreateMutation(token, bundle.workspace.id, "products", {
    ...payload,
    variants: parseSimpleVariants(payload.variantsText),
    modifierGroups: parseSimpleModifiers(payload.modifiersText),
    status: "active",
  }, onRefresh);
  return <FormGrid onSubmit={() => mutation.mutate()} pending={mutation.isPending} error={mutation.error?.message}>
    <Field label="Producto" value={payload.name} onChange={(name) => setPayload((current) => ({ ...current, name }))} placeholder="Camiseta negra M" />
    <Field label="Precio" value={payload.price} onChange={(price) => setPayload((current) => ({ ...current, price }))} placeholder="45000" />
    <Field label="Stock" value={payload.stock} onChange={(stock) => setPayload((current) => ({ ...current, stock }))} placeholder="10" />
    <Field label="Categoría" value={payload.category} onChange={(category) => setPayload((current) => ({ ...current, category }))} placeholder="Camisetas" />
    <Field label="Imagen URL" value={payload.imageUrl} onChange={(imageUrl) => setPayload((current) => ({ ...current, imageUrl }))} placeholder="https://..." />
    <Field label="Descripción" value={payload.description} onChange={(description) => setPayload((current) => ({ ...current, description }))} placeholder="Descripción corta" />
    <Field label="Variantes" value={payload.variantsText} onChange={(variantsText) => setPayload((current) => ({ ...current, variantsText }))} placeholder="Negra M | sku-001 | 0" />
    <Field label="Modificadores" value={payload.modifiersText} onChange={(modifiersText) => setPayload((current) => ({ ...current, modifiersText }))} placeholder="Extra queso | 2000" />
  </FormGrid>;
}

function CustomerForm({ bundle, token, onRefresh }: FormProps) {
  const [payload, setPayload] = useState({ name: "", phone: "", consentStatus: "granted" });
  const mutation = useCreateMutation(token, bundle.workspace.id, "customers", payload, onRefresh);
  return <FormGrid onSubmit={() => mutation.mutate()} pending={mutation.isPending} error={mutation.error?.message}>
    <Field label="Cliente" value={payload.name} onChange={(name) => setPayload((current) => ({ ...current, name }))} placeholder="María Pérez" />
    <Field label="WhatsApp" value={payload.phone} onChange={(phone) => setPayload((current) => ({ ...current, phone }))} placeholder="+57300..." />
    <Field label="Consentimiento" value={payload.consentStatus} onChange={(consentStatus) => setPayload((current) => ({ ...current, consentStatus }))} placeholder="granted / unknown / revoked" />
  </FormGrid>;
}

function OrderForm({ bundle, token, onRefresh }: FormProps) {
  const firstProduct = bundle.products[0];
  const [payload, setPayload] = useState({ customerName: "", itemName: String(firstProduct?.name || ""), price: productPriceInput(firstProduct), quantity: "1" });
  const mutation = useMutation({
    mutationFn: () => createTienditaEntity(token, bundle.workspace.id, "orders", {
      customerName: payload.customerName,
      items: [{ name: payload.itemName, quantity: payload.quantity, price: payload.price }],
      status: "pending_payment",
      paymentStatus: "pending",
    }),
    onSuccess: onRefresh,
  });
  return <FormGrid onSubmit={() => mutation.mutate()} pending={mutation.isPending} error={mutation.error?.message}>
    <Field label="Cliente" value={payload.customerName} onChange={(customerName) => setPayload((current) => ({ ...current, customerName }))} placeholder="Ana" />
    <Field label="Item" value={payload.itemName} onChange={(itemName) => setPayload((current) => ({ ...current, itemName }))} placeholder="Camiseta negra" />
    <Field label="Precio" value={payload.price} onChange={(price) => setPayload((current) => ({ ...current, price }))} placeholder="45000" />
    <Field label="Cantidad" value={payload.quantity} onChange={(quantity) => setPayload((current) => ({ ...current, quantity }))} placeholder="1" />
  </FormGrid>;
}

function BroadcastForm({ bundle, token, onRefresh }: FormProps) {
  const [payload, setPayload] = useState({ title: "Promo de la semana", message: "Hola, tenemos productos disponibles para entrega inmediata. ¿Te paso el catálogo?" });
  const mutation = useCreateMutation(token, bundle.workspace.id, "broadcasts", { ...payload, dryRun: true }, onRefresh);
  return <FormGrid onSubmit={() => mutation.mutate()} pending={mutation.isPending} error={mutation.error?.message}>
    <Field label="Título" value={payload.title} onChange={(title) => setPayload((current) => ({ ...current, title }))} placeholder="Menú del día" />
    <div className="sm:col-span-2">
      <label className="space-y-1.5">
        <span className="text-xs font-bold text-slate-300">Mensaje</span>
        <Textarea value={payload.message} onChange={(event) => setPayload((current) => ({ ...current, message: event.target.value }))} placeholder="Hoy tenemos..." className="rounded-2xl border-white/10 bg-black/20" />
      </label>
    </div>
  </FormGrid>;
}

function ProfileForm({ bundle, token, onRefresh }: FormProps) {
  const [payload, setPayload] = useState({
    businessName: String(bundle.profile.businessName || bundle.workspace.name),
    publicSlug: String(bundle.profile.publicSlug || slugify(bundle.workspace.name)),
    whatsappPhone: String(bundle.profile.whatsappPhone || bundle.workspace.ownerPhone || ""),
    address: String(bundle.profile.address || ""),
    deliveryPromise: String(bundle.profile.deliveryPromise || "Entrega hoy o mañana según disponibilidad."),
    paymentMethods: Array.isArray(bundle.profile.paymentMethods) ? bundle.profile.paymentMethods.join(", ") : String(bundle.profile.paymentMethods || "WhatsApp, Mercado Pago/transferencia por confirmar"),
    paymentLink: String(bundle.profile.paymentLink || ""),
    paymentInstructions: String(bundle.profile.paymentInstructions || ""),
    deliveryZones: JSON.stringify(bundle.profile.deliveryZones || [{ name: "Zona centro", keywords: ["centro"], feeCents: 500000 }], null, 2),
    taxRules: JSON.stringify(bundle.profile.taxRules || [{ name: "IVA", country: "CO", rateBps: 0, includedInPrice: false }], null, 2),
    businessHours: JSON.stringify(bundle.profile.businessHours || { mon: ["09:00-18:00"], tue: ["09:00-18:00"], wed: ["09:00-18:00"], thu: ["09:00-18:00"], fri: ["09:00-18:00"] }, null, 2),
    branches: JSON.stringify(bundle.profile.branches || [{ id: "principal", name: "Principal", active: true }], null, 2),
    notificationChannels: JSON.stringify(bundle.profile.notificationChannels || [{ channel: "dashboard", target: "seller-dashboard", enabled: true }], null, 2),
    storefrontEnabled: "true",
    manualModeAcceptedAt: new Date().toISOString(),
  });
  const mutation = useMutation({
    mutationFn: () => patchTienditaProfile(token, bundle.workspace.id, {
      businessName: payload.businessName,
      publicSlug: payload.publicSlug,
      whatsappPhone: payload.whatsappPhone,
      address: payload.address,
      deliveryPromise: payload.deliveryPromise,
      paymentMethods: payload.paymentMethods,
      paymentLink: payload.paymentLink,
      paymentInstructions: payload.paymentInstructions,
      deliveryZones: parseJsonOrText(payload.deliveryZones),
      taxRules: parseJsonOrText(payload.taxRules),
      businessHours: parseJsonOrText(payload.businessHours),
      branches: parseJsonOrText(payload.branches),
      notificationChannels: parseJsonOrText(payload.notificationChannels),
      storefrontEnabled: payload.storefrontEnabled === "true",
      manualModeAcceptedAt: payload.manualModeAcceptedAt,
    }),
    onSuccess: onRefresh,
  });
  return <FormGrid onSubmit={() => mutation.mutate()} pending={mutation.isPending} error={mutation.error?.message}>
    <Field label="Nombre del negocio" value={payload.businessName} onChange={(businessName) => setPayload((current) => ({ ...current, businessName }))} placeholder="Tienda de Ana" />
    <Field label="Slug público" value={payload.publicSlug} onChange={(publicSlug) => setPayload((current) => ({ ...current, publicSlug: slugify(publicSlug) }))} placeholder="tienda-de-ana" />
    <Field label="WhatsApp visible" value={payload.whatsappPhone} onChange={(whatsappPhone) => setPayload((current) => ({ ...current, whatsappPhone }))} placeholder="+57300..." />
    <Field label="Dirección o zona" value={payload.address} onChange={(address) => setPayload((current) => ({ ...current, address }))} placeholder="Laureles, Medellín" />
    <Field label="Promesa de entrega" value={payload.deliveryPromise} onChange={(deliveryPromise) => setPayload((current) => ({ ...current, deliveryPromise }))} placeholder="Entrega el mismo día" />
    <Field label="Métodos de pago" value={payload.paymentMethods} onChange={(paymentMethods) => setPayload((current) => ({ ...current, paymentMethods }))} placeholder="Nequi, transferencia, efectivo" />
    <Field label="Link de cobro" value={payload.paymentLink} onChange={(paymentLink) => setPayload((current) => ({ ...current, paymentLink }))} placeholder="https://mpago.la/..." />
    <Field label="Instrucciones de pago" value={payload.paymentInstructions} onChange={(paymentInstructions) => setPayload((current) => ({ ...current, paymentInstructions }))} placeholder="Paga y envía soporte por WhatsApp." />
    <Field label="Zonas delivery JSON" value={payload.deliveryZones} onChange={(deliveryZones) => setPayload((current) => ({ ...current, deliveryZones }))} placeholder='[{"name":"Centro","feeCents":500000}]' />
    <Field label="Impuestos JSON" value={payload.taxRules} onChange={(taxRules) => setPayload((current) => ({ ...current, taxRules }))} placeholder='[{"name":"IVA","rateBps":1900}]' />
    <Field label="Horarios JSON" value={payload.businessHours} onChange={(businessHours) => setPayload((current) => ({ ...current, businessHours }))} placeholder='{"mon":["09:00-18:00"]}' />
    <Field label="Sucursales JSON" value={payload.branches} onChange={(branches) => setPayload((current) => ({ ...current, branches }))} placeholder='[{"id":"principal","name":"Principal"}]' />
    <Field label="Notificaciones JSON" value={payload.notificationChannels} onChange={(notificationChannels) => setPayload((current) => ({ ...current, notificationChannels }))} placeholder='[{"channel":"telegram","target":"..."}]' />
    <Field label="Tienda pública" value={payload.storefrontEnabled} onChange={(storefrontEnabled) => setPayload((current) => ({ ...current, storefrontEnabled }))} placeholder="true / false" />
    <Field label="Modo manual aceptado" value={payload.manualModeAcceptedAt} onChange={(manualModeAcceptedAt) => setPayload((current) => ({ ...current, manualModeAcceptedAt }))} placeholder="ISO date" />
  </FormGrid>;
}

function OpsForm({ bundle, token, onRefresh }: FormProps) {
  const [query, setQuery] = useState("");
  const [coverage, setCoverage] = useState("");
  const [result, setResult] = useState("");
  const priceMutation = useMutation({
    mutationFn: () => runTienditaCommerceTool(token, bundle.workspace.id, "consultar_precio", { query }),
    onSuccess: (response) => setResult(String(response.reply || JSON.stringify(response))),
  });
  const coverageMutation = useMutation({
    mutationFn: () => runTienditaCommerceTool(token, bundle.workspace.id, "verificar_cobertura", { deliveryAddress: coverage }),
    onSuccess: (response) => setResult(String(response.reply || JSON.stringify(response))),
  });
  const abandonmentMutation = useMutation({
    mutationFn: () => queueTienditaAbandonmentReminders(token, bundle.workspace.id, { ageMs: 0 }),
    onSuccess: (response) => {
      setResult(`Recordatorios encolados: ${String(response.abandonedCarts || 0)}`);
      onRefresh();
    },
  });
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field label="Consultar precio" value={query} onChange={setQuery} placeholder="camiseta negra" />
      <Field label="Verificar cobertura" value={coverage} onChange={setCoverage} placeholder="Centro, Medellín" />
      <Button className="rounded-2xl" disabled={!query || priceMutation.isPending} onClick={() => priceMutation.mutate()}>Consultar precio</Button>
      <Button className="rounded-2xl" disabled={!coverage || coverageMutation.isPending} onClick={() => coverageMutation.mutate()}>Ver cobertura</Button>
      <Button className="rounded-2xl sm:col-span-2" disabled={abandonmentMutation.isPending} onClick={() => abandonmentMutation.mutate()}>Procesar carritos abandonados</Button>
      {result ? <div className="sm:col-span-2"><StatusMessage>{result}</StatusMessage></div> : null}
      {priceMutation.error || coverageMutation.error || abandonmentMutation.error ? <div className="sm:col-span-2"><StatusMessage tone="error">{priceMutation.error?.message || coverageMutation.error?.message || abandonmentMutation.error?.message}</StatusMessage></div> : null}
    </div>
  );
}

function DataSections({ bundle, token, onRefresh }: { bundle: TienditaBundle; token: string; onRefresh: () => void }) {
  return (
    <div className="grid gap-6 xl:grid-cols-2">
      <ListCard title="Productos" items={bundle.products} empty="Agrega tu primer producto." fields={["name", "priceCents", "price", "stock", "status"]} />
      <ListCard title="Carritos activos" items={bundle.carts || []} empty="Todavía no hay carritos persistentes." fields={["customerName", "totalCents", "status", "paymentStatus"]} />
      <ListCard title="Pedidos" items={bundle.orders} empty="Crea un pedido manual." fields={["customerName", "totalCents", "status", "paymentStatus"]} />
      <ListCard title="Clientes" items={bundle.customers} empty="Registra tu primer cliente." fields={["name", "phone", "consentStatus"]} />
      <ListCard title="Recibos" items={bundle.receipts} empty="Genera un recibo." fields={["number", "amountCents", "status"]} />
      <ListCard title="Broadcasts" items={bundle.broadcasts} empty="Crea un menú o promoción." fields={["title", "status", "sentCount"]} />
      <ListCard title="Ledger de inventario" items={bundle.inventoryMovements || []} empty="Sin movimientos de inventario." fields={["type", "quantityDelta", "stockBefore", "stockAfter"]} />
      <ListCard title="Eventos de pago" items={bundle.paymentEvents || []} empty="Sin eventos de pago todavía." fields={["provider", "status", "verified", "amountCents"]} />
      <ListCard title="Status operativo" items={bundle.statusChecks || []} empty="Sin checks de status." fields={["service", "status", "message"]} />
      <ListCard title="Auditoría" items={bundle.auditLogs || []} empty="Sin eventos auditados." fields={["action", "entity", "actorType"]} />
      <IntegrationsCard bundle={bundle} token={token} onRefresh={onRefresh} />
      <LoyaltyStampCard bundle={bundle} token={token} onRefresh={onRefresh} />
    </div>
  );
}

function LoyaltyStampCard({ bundle, token, onRefresh }: { bundle: TienditaBundle; token: string; onRefresh: () => void }) {
  const firstCustomer = String(bundle.customers[0]?.id || "");
  const [customerId, setCustomerId] = useState(firstCustomer);
  const mutation = useMutation({ mutationFn: () => addLoyaltyStamp(token, bundle.workspace.id, { customerId, stamps: 1 }), onSuccess: onRefresh });
  return (
    <Card className="rounded-[1.5rem] border-white/10 bg-white/[0.045]">
      <CardHeader>
        <CardTitle>Fidelización</CardTitle>
        <CardDescription>Registra un sello para un cliente existente.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Input value={customerId} onChange={(event) => setCustomerId(event.target.value)} placeholder="customerId" className="rounded-2xl border-white/10 bg-black/20" />
        <Button className="w-full rounded-2xl" disabled={!customerId || mutation.isPending} onClick={() => mutation.mutate()}>Agregar sello</Button>
        {mutation.error ? <StatusMessage tone="error">{mutation.error.message}</StatusMessage> : null}
      </CardContent>
    </Card>
  );
}

function IntegrationsCard({ bundle, token, onRefresh }: { bundle: TienditaBundle; token: string; onRefresh: () => void }) {
  const [provider, setProvider] = useState("whatsapp");
  const mutation = useMutation({
    mutationFn: (selectedProvider: string) => upsertIntegration(token, bundle.workspace.id, { provider: selectedProvider, status: "pending", label: integrationLabel(selectedProvider) }),
    onSuccess: onRefresh,
  });
  return (
    <Card className="rounded-[1.5rem] border-white/10 bg-white/[0.045]">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Settings className="h-5 w-5" /> Conexión asistida</CardTitle>
        <CardDescription>Empieza en modo manual: copia mensajes y cobra por WhatsApp. La API oficial se conecta después.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {bundle.integrations.length ? bundle.integrations.map((item) => (
          <div key={String(item.id || item.provider)} className="flex items-center justify-between rounded-2xl border border-white/10 bg-black/20 p-3 text-sm">
            <span>{String(item.label || item.provider)}</span>
            <span className="rounded-full bg-white/10 px-3 py-1 text-xs">{String(item.status)}</span>
          </div>
        )) : <p className="text-sm text-slate-400">Todavía no hay integraciones conectadas.</p>}
        <div className="grid gap-2 sm:grid-cols-2">
          {["whatsapp", "telegram", "mercadopago", "wompi", "stripe", "google_sheets"].map((item) => (
            <Button key={item} variant={provider === item ? "default" : "outline"} className="rounded-2xl" disabled={mutation.isPending} onClick={() => { setProvider(item); mutation.mutate(item); }}>
              {mutation.isPending && provider === item ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MessageCircle className="mr-2 h-4 w-4" />}
              {integrationLabel(item)}
            </Button>
          ))}
        </div>
        {mutation.error ? <StatusMessage tone="error">{mutation.error.message}</StatusMessage> : null}
      </CardContent>
    </Card>
  );
}

function ListCard({ title, items, empty, fields }: { title: string; items: Array<Record<string, unknown>>; empty: string; fields: string[] }) {
  return (
    <Card className="rounded-[1.5rem] border-white/10 bg-white/[0.045]">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{items.length ? `${items.length} registro(s)` : empty}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {items.length ? items.slice(0, 6).map((item) => (
          <div key={String(item.id || item.name || item.title)} className="rounded-2xl border border-white/10 bg-black/20 p-3 text-sm">
            <p className="font-black text-white">{String(item.name || item.customerName || item.title || item.number || item.id)}</p>
            <p className="mt-1 text-xs leading-5 text-slate-400">{fields.map((field) => `${field}: ${String(item[field] ?? "-")}`).join(" · ")}</p>
          </div>
        )) : <p className="text-sm text-slate-400">{empty}</p>}
      </CardContent>
    </Card>
  );
}

function WizardProgress({ step }: { step: number }) {
  return (
    <div className="grid gap-2 sm:grid-cols-5">
      {WIZARD_STEPS.map((label, index) => (
        <div key={label} className={`rounded-2xl border p-3 text-xs font-black ${index <= step ? "border-emerald-200/40 bg-emerald-300/15 text-emerald-50" : "border-white/10 bg-black/20 text-slate-500"}`}>
          <span className="mr-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/10">{index + 1}</span>
          {label}
        </div>
      ))}
    </div>
  );
}

function LaunchSidecar({ businessName, template, products }: { businessName: string; template: VerticalTemplate; products: Array<{ name: string; price: string; stock: string; description: string }> }) {
  return (
    <div className="space-y-6">
      <Card className="rounded-[2rem] border-white/10 bg-black/25">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-emerald-200" />
            Pack {template.label}
          </CardTitle>
          <CardDescription>{template.tagline}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm leading-6 text-slate-300">Incluye categorías, mensaje inicial, productos demo, link compartible y simulación de compra.</p>
          <ProductPreview products={products} />
        </CardContent>
      </Card>
      <WhatsAppPreview businessName={businessName} template={template} products={products} />
    </div>
  );
}

function ReadyPreview({ businessName, template, products }: { businessName: string; template: VerticalTemplate; products: Array<{ name: string; price: string; stock: string; description: string }> }) {
  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <ReadyTile icon={<Store className="h-5 w-5" />} title="Tienda" text={`${businessName} con ${products.length} productos iniciales.`} />
      <ReadyTile icon={<MessageCircle className="h-5 w-5" />} title="WhatsApp" text={template.firstReply} />
      <ReadyTile icon={<QrCode className="h-5 w-5" />} title="Link + QR" text="Página lista para compartir con clientes." />
    </div>
  );
}

function ReadyTile({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-black/20 p-4">
      <div className="mb-3 inline-flex rounded-2xl bg-emerald-300/10 p-3 text-emerald-200">{icon}</div>
      <p className="font-black">{title}</p>
      <p className="mt-2 text-sm leading-6 text-slate-300">{text}</p>
    </div>
  );
}

function WhatsAppPreview({ businessName, template, products }: { businessName: string; template: VerticalTemplate; products: Array<{ name: string; price: string; stock: string; description: string }> }) {
  const [simulatedReply, setSimulatedReply] = useState(false);
  const firstProduct = products[0] || template.products[0];
  return (
    <Card className="rounded-[2rem] border-emerald-200/20 bg-[#071b17]">
      <CardHeader className="border-b border-white/10">
        <CardTitle className="flex items-center gap-2 text-lg">
          <MessageCircle className="h-5 w-5 text-emerald-200" />
          Simulador WhatsApp
        </CardTitle>
        <CardDescription>Así se ve la compra para el cliente final.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 pt-5">
        <ChatBubble side="left" title={businessName}>{template.firstReply}</ChatBubble>
        <ChatBubble side="right">Hola, quiero ver {template.hero.toLowerCase()}.</ChatBubble>
        <ChatBubble side="left" title={businessName}>
          Tengo {firstProduct.name} por {formatMoney(firstProduct.price)}. {firstProduct.description} ¿Confirmo pedido?
        </ChatBubble>
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3">
          <p className="text-xs uppercase tracking-[0.16em] text-emerald-200">Resumen automático</p>
          <p className="mt-2 font-black">{firstProduct.name}</p>
          <p className="text-sm text-slate-300">Total: {formatMoney(firstProduct.price)} · Estado: pendiente de pago</p>
        </div>
        {simulatedReply ? (
          <ChatBubble side="right">Sí, confirmo. Envíame el link de pago y el recibo.</ChatBubble>
        ) : null}
        {simulatedReply ? (
          <ChatBubble side="left" title={businessName}>
            Pedido registrado. Te comparto el resumen y el link de pago para finalizar por WhatsApp.
          </ChatBubble>
        ) : null}
        <Button className="w-full rounded-2xl bg-emerald-300 font-black text-slate-950 hover:bg-white" onClick={() => setSimulatedReply((current) => !current)} type="button">
          <Send className="mr-2 h-4 w-4" />
          {simulatedReply ? "Reiniciar simulación" : "Simular respuesta"}
        </Button>
      </CardContent>
    </Card>
  );
}

function ChatBubble({ children, side, title }: { children: React.ReactNode; side: "left" | "right"; title?: string }) {
  return (
    <div className={`flex ${side === "right" ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[82%] rounded-3xl px-4 py-3 text-sm leading-6 ${side === "right" ? "bg-emerald-300 text-slate-950" : "bg-white/10 text-slate-100"}`}>
        {title ? <p className="mb-1 text-xs font-black uppercase tracking-[0.14em] opacity-70">{title}</p> : null}
        {children}
      </div>
    </div>
  );
}

function ProductPreview({ products }: { products: Array<{ name: string; price: string; stock: string; description: string }> }) {
  return (
    <div className="space-y-2">
      {products.slice(0, 4).map((product) => (
        <div key={`${product.name}-${product.price}`} className="flex items-start justify-between gap-3 rounded-2xl border border-white/10 bg-black/20 p-3 text-sm">
          <div>
            <p className="font-black text-white">{product.name}</p>
            <p className="mt-1 text-xs leading-5 text-slate-400">{product.description || "Sin descripción"} · Stock {product.stock || "0"}</p>
          </div>
          <span className="rounded-full bg-emerald-300/10 px-3 py-1 text-xs font-black text-emerald-100">{formatMoney(product.price)}</span>
        </div>
      ))}
      {!products.length ? <p className="text-sm text-slate-400">No detecté productos todavía.</p> : null}
    </div>
  );
}

type FormProps = { bundle: TienditaBundle; token: string; onRefresh: () => void };

function useCreateMutation(token: string, workspaceId: string, collection: "products" | "customers" | "orders" | "receipts" | "broadcasts", payload: Record<string, unknown>, onRefresh: () => void) {
  return useMutation({ mutationFn: () => createTienditaEntity(token, workspaceId, collection, payload), onSuccess: onRefresh });
}

function FormGrid({ children, onSubmit, pending, error }: { children: React.ReactNode; onSubmit: () => void; pending: boolean; error?: string }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {children}
      <Button className="h-11 rounded-2xl bg-emerald-300 font-black text-slate-950 hover:bg-white sm:col-span-2" disabled={pending} onClick={onSubmit}>
        {pending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
        Guardar
      </Button>
      {error ? <div className="sm:col-span-2"><StatusMessage tone="error">{error}</StatusMessage></div> : null}
    </div>
  );
}

function Field({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (value: string) => void; placeholder?: string }) {
  return (
    <label className="space-y-1.5">
      <span className="text-xs font-bold text-slate-300">{label}</span>
      <Input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} className="h-11 rounded-2xl border-white/10 bg-black/20" />
    </label>
  );
}

function Metric({ label, value, icon }: { label: string; value: string; icon: React.ReactNode }) {
  return (
    <Card className="rounded-[1.5rem] border-white/10 bg-white/[0.055]">
      <CardContent className="flex items-center justify-between p-5">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500">{label}</p>
          <p className="mt-2 text-2xl font-black">{value}</p>
        </div>
        <div className="rounded-2xl bg-emerald-300/10 p-3 text-emerald-200">{icon}</div>
      </CardContent>
    </Card>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return <span className="inline-flex w-fit rounded-full border border-emerald-200/20 bg-emerald-300/10 px-3 py-1 text-xs font-black uppercase tracking-[0.16em] text-emerald-100">{children}</span>;
}

function LoadingState({ label }: { label: string }) {
  return <div className="flex min-h-[420px] items-center justify-center"><Loader2 className="mr-3 h-8 w-8 animate-spin text-emerald-200" />{label}</div>;
}

function StatusMessage({ children, tone = "info" }: { children: React.ReactNode; tone?: "info" | "error" }) {
  const classes = tone === "error" ? "border-red-300/20 bg-red-500/10 text-red-100" : "border-cyan-200/20 bg-cyan-300/10 text-cyan-50";
  return <div className={`rounded-2xl border p-3 text-sm leading-6 ${classes}`}>{children}</div>;
}

function parseCatalogText(text: string) {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split(/\s[-|,;]\s|;/).map((part) => part.trim()).filter(Boolean);
      const priceIndex = parts.findIndex((part, index) => index > 0 && extractCatalogPrice(part));
      const price = priceIndex > -1 ? extractCatalogPrice(parts[priceIndex]) : extractCatalogPrice(line);
      const name = (priceIndex > 0 ? parts.slice(0, priceIndex).join(" - ") : line.replace(price, "").split(/\s[-|,;]\s|;/)[0] || line).replace(/[-,;|:]+$/, "").trim();
      const stockMatch = line.match(/(?:stock|disponible|disponibles|cant(?:idad)?|cupos?)\s*:?\s*(\d+)/i);
      const stock = stockMatch?.[1] || (priceIndex > -1 ? parts.slice(priceIndex + 1).find((part) => /^\d+$/.test(part.replace(/\D/g, "")))?.replace(/\D/g, "") : "") || "0";
      const description = parts.filter((part, index) => index !== priceIndex && part !== name && part !== stock).join(" · ").replace(/(?:stock|disponible|disponibles|cant(?:idad)?|cupos?)\s*:?\s*\d+/i, "").trim();
      return { name: name || "Producto", price: price || "0", stock, description };
    });
}

function parseSimpleVariants(text: string) {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      const [label, sku, priceDelta] = line.split("|").map((part) => part.trim());
      return {
        id: slugify(label || `variante-${index + 1}`),
        label: label || `Variante ${index + 1}`,
        sku: sku || undefined,
        priceDeltaCents: Number(priceDelta || 0) * 100,
      };
    });
}

function parseSimpleModifiers(text: string) {
  const options = text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, index) => {
      const [label, price] = line.split("|").map((part) => part.trim());
      return {
        id: slugify(label || `extra-${index + 1}`),
        label: label || `Extra ${index + 1}`,
        priceCents: Number(price || 0) * 100,
      };
    });
  return options.length ? [{ id: "extras", label: "Extras", min: 0, max: options.length, options }] : [];
}

function parseJsonOrText(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return "";
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return trimmed;
  }
}

function integrationLabel(provider: string) {
  const labels: Record<string, string> = {
    whatsapp: "WhatsApp",
    telegram: "Telegram",
    mercadopago: "Mercado Pago",
    wompi: "Wompi",
    stripe: "Stripe",
    google_sheets: "Google Sheets",
  };
  return labels[provider] || provider;
}

function extractCatalogPrice(value: string) {
  const match = value.match(/(?:\$|cop|usd|precio\s*:?\s*)?\s*(\d{1,3}(?:[.,]\d{3})+|\d{4,}(?:[.,]\d{1,2})?)/i);
  return match?.[1]?.replace(/[^\d.]/g, "").replace(/\.(?=\d{3}\b)/g, "") || "";
}

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function getTemplateFromBundle(bundle: TienditaBundle) {
  const type = String(bundle.profile.businessType || bundle.profile.templateId || "").toLowerCase();
  if (type in VERTICAL_TEMPLATES) return VERTICAL_TEMPLATES[type as VerticalKey];
  return VERTICAL_TEMPLATES.ropa;
}

function productsForPreview(bundle: TienditaBundle, template: VerticalTemplate) {
  const products = bundle.products.slice(0, 4).map((product) => ({
    name: String(product.name || "Producto"),
    price: productPriceInput(product),
    stock: String(product.stock || "0"),
    description: String(product.description || ""),
  }));
  return products.length ? products : template.products;
}

function productPriceInput(product?: Record<string, unknown>) {
  if (!product) return "";
  if (product.price !== undefined && product.price !== null && product.price !== "") return String(product.price);
  const cents = Number(product.priceCents || 0);
  return cents > 0 ? String(cents / 100) : "";
}

function buildStoreLink(bundle: TienditaBundle) {
  if (bundle.publicStore?.publicLink) return bundle.publicStore.publicLink;
  const slug = String(bundle.profile.publicSlug || bundle.profile.slug || bundle.workspace.name || bundle.workspace.id).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  const origin = typeof window === "undefined" ? "https://tech.alquim-ia.cloud/oracle" : `${window.location.origin}${window.location.pathname.startsWith("/oracle") ? "/oracle" : ""}`;
  return `${origin}/tienditabot/store/${slug || bundle.workspace.id}`;
}

function sumRecords(records: Array<Record<string, unknown>>, fields: string[]) {
  return records.reduce((total, record) => {
    const value = fields.map((field) => Number(record[field] || 0)).find((candidate) => Number.isFinite(candidate) && candidate > 0) || 0;
    return total + value;
  }, 0);
}

function moneyFromCents(value: number, bundle: TienditaBundle) {
  const currency = String(bundle.profile.currency || "COP");
  const normalized = value > 999 ? value / 100 : value;
  return new Intl.NumberFormat("es-CO", { style: "currency", currency, maximumFractionDigits: 0 }).format(normalized);
}

function formatMoney(value: string) {
  const numeric = Number(value || 0);
  return new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(numeric);
}
